import { useEffect, useMemo, useState } from 'react';
import { Layout } from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from '@/hooks/use-toast';
import { bannersApi, type AdminBanner } from '../api';
import { env } from '@/config/env';
import { Plus, Loader2, Pencil, Trash2, ArrowUp, ArrowDown } from 'lucide-react';

function toAbsoluteUrl(u?: string) {
  if (!u) return '';
  if (/^https?:\/\//i.test(u)) return u;
  const base = env.apiUrl.replace(/\/$/, '');
  return `${base}/${u.replace(/^\//, '')}`;
}

type FormState = {
  title: string;
  link_url: string;
  is_active: boolean;
  desktop_image: File | null;
  mobile_image: File | null;
};

const emptyForm = (): FormState => ({
  title: '',
  link_url: '',
  is_active: true,
  desktop_image: null,
  mobile_image: null,
});

export default function BannersAdminPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [banners, setBanners] = useState<AdminBanner[]>([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<AdminBanner | null>(null);
  const [deleting, setDeleting] = useState<AdminBanner | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);

  const sorted = useMemo(
    () => [...banners].sort((a, b) => Number(a.sort_order) - Number(b.sort_order)),
    [banners]
  );

  const load = async () => {
    setLoading(true);
    try {
      const items = await bannersApi.listAdmin();
      setBanners(items);
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error?.response?.data?.message || 'No se pudieron cargar los banners.',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    setOpen(true);
  };

  const openEdit = (banner: AdminBanner) => {
    setEditing(banner);
    setForm({
      title: banner.title || '',
      link_url: banner.link_url || '',
      is_active: banner.is_active !== false,
      desktop_image: null,
      mobile_image: null,
    });
    setOpen(true);
  };

  const save = async () => {
    if (!editing && (!form.desktop_image || !form.mobile_image)) {
      toast({
        variant: 'destructive',
        title: 'Validación',
        description: 'Debes subir desktop_image y mobile_image.',
      });
      return;
    }

    if (form.link_url && !/^https?:\/\//i.test(form.link_url)) {
      toast({
        variant: 'destructive',
        title: 'Validación',
        description: 'link_url debe iniciar con http:// o https://',
      });
      return;
    }

    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('title', form.title);
      fd.append('link_url', form.link_url);
      fd.append('is_active', String(form.is_active));
      if (form.desktop_image) fd.append('desktop_image', form.desktop_image);
      if (form.mobile_image) fd.append('mobile_image', form.mobile_image);

      if (editing) {
        await bannersApi.update(editing.id, fd);
        toast({ title: 'Banner actualizado' });
      } else {
        await bannersApi.create(fd);
        toast({ title: 'Banner creado' });
      }

      setOpen(false);
      await load();
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error?.response?.data?.message || 'No se pudo guardar el banner.',
      });
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (banner: AdminBanner, isActive: boolean) => {
    try {
      await bannersApi.update(banner.id, { is_active: isActive });
      setBanners((prev) => prev.map((b) => (b.id === banner.id ? { ...b, is_active: isActive } : b)));
    } catch {
      toast({ variant: 'destructive', title: 'Error', description: 'No se pudo actualizar el estado.' });
    }
  };

  const remove = async (banner: AdminBanner) => {
    try {
      await bannersApi.remove(banner.id);
      toast({ title: 'Banner eliminado' });
      await load();
    } catch {
      toast({ variant: 'destructive', title: 'Error', description: 'No se pudo eliminar el banner.' });
    }
  };

  const reorder = async (next: AdminBanner[]) => {
    setBanners(next);
    try {
      await bannersApi.reorder(next.map((x) => x.id));
      await load();
    } catch {
      toast({ variant: 'destructive', title: 'Error', description: 'No se pudo guardar el orden.' });
      await load();
    }
  };

  const move = async (index: number, dir: -1 | 1) => {
    const target = index + dir;
    if (target < 0 || target >= sorted.length) return;
    const next = [...sorted];
    const tmp = next[index];
    next[index] = next[target];
    next[target] = tmp;
    await reorder(next);
  };

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8 space-y-6">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h1 className="text-3xl font-bold">Banners del Portal</h1>
            <p className="text-muted-foreground">Gestiona banners para home/listado.</p>
          </div>
          <Button onClick={openCreate}>
            <Plus className="h-4 w-4 mr-2" />
            Nuevo banner
          </Button>
        </div>

        {loading ? (
          <Card className="p-6 flex items-center gap-2 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Cargando banners...
          </Card>
        ) : sorted.length === 0 ? (
          <Card className="p-8 text-center text-muted-foreground">No hay banners cargados.</Card>
        ) : (
          <div className="space-y-4">
            {sorted.map((banner, index) => (
              <Card key={banner.id} className="p-4">
                <div className="grid grid-cols-1 xl:grid-cols-[1fr,1fr,auto] gap-4 items-start">
                  <div className="space-y-2">
                    <p className="text-xs uppercase text-muted-foreground">Desktop</p>
                    <img
                      src={toAbsoluteUrl(banner.desktop_image_path)}
                      alt={banner.title || banner.id}
                      className="w-full h-32 object-cover rounded-md border"
                    />
                  </div>

                  <div className="space-y-2">
                    <p className="text-xs uppercase text-muted-foreground">Mobile</p>
                    <div className="w-[150px] aspect-[4/5] rounded-md border overflow-hidden bg-muted/40">
                      <img
                        src={toAbsoluteUrl(banner.mobile_image_path)}
                        alt={banner.title || banner.id}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>

                  <div className="flex xl:flex-col gap-2 xl:items-end">
                    <Button variant="outline" size="sm" onClick={() => move(index, -1)} disabled={index === 0}>
                      <ArrowUp className="h-4 w-4" />
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => move(index, 1)} disabled={index === sorted.length - 1}>
                      <ArrowDown className="h-4 w-4" />
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => openEdit(banner)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="destructive" size="sm" onClick={() => setDeleting(banner)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                  <div>
                    <p className="text-muted-foreground">Título</p>
                    <p>{banner.title || '—'}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Link</p>
                    <p className="truncate">{banner.link_url || '—'}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch checked={banner.is_active !== false} onCheckedChange={(v) => toggleActive(banner, v)} />
                    <span>{banner.is_active !== false ? 'Activo' : 'Inactivo'}</span>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? 'Editar banner' : 'Nuevo banner'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Título (opcional)</Label>
              <Input value={form.title} onChange={(e) => setForm((s) => ({ ...s, title: e.target.value }))} />
            </div>

            <div className="space-y-2">
              <Label>Link URL (opcional)</Label>
              <Input
                placeholder="https://..."
                value={form.link_url}
                onChange={(e) => setForm((s) => ({ ...s, link_url: e.target.value }))}
              />
            </div>

            <div className="flex items-center gap-2">
              <Switch checked={form.is_active} onCheckedChange={(v) => setForm((s) => ({ ...s, is_active: v }))} />
              <span>{form.is_active ? 'Activo' : 'Inactivo'}</span>
            </div>

            <div className="space-y-2">
              <Label>Imagen Desktop {editing ? '(reemplazo opcional)' : '(obligatoria)'}</Label>
              <Input
                type="file"
                accept="image/*"
                onChange={(e) => setForm((s) => ({ ...s, desktop_image: e.target.files?.[0] || null }))}
              />
              <p className="text-xs text-muted-foreground">Dimensión recomendada: 2400 x 800 (3:1). Área segura centrada para textos.</p>
              <p className="text-xs text-muted-foreground">
                Safe area desktop: mantén textos/logos dentro del 70% central del ancho y 80% central del alto
                (deja ~15% en laterales y ~10% arriba/abajo).
              </p>
            </div>

            <div className="space-y-2">
              <Label>Imagen Mobile {editing ? '(reemplazo opcional)' : '(obligatoria)'}</Label>
              <Input
                type="file"
                accept="image/*"
                onChange={(e) => setForm((s) => ({ ...s, mobile_image: e.target.files?.[0] || null }))}
              />
              <p className="text-xs text-muted-foreground">Dimensión recomendada: 1080 x 1350 (4:5). Evita texto pegado aos bordes.</p>
              <p className="text-xs text-muted-foreground">
                Safe area mobile: mantén textos/logos dentro del 84% central del ancho y 74% central del alto
                (deja ~8% en laterales y ~13% arriba/abajo).
              </p>
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
              <Button onClick={save} disabled={saving}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Guardar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleting} onOpenChange={(v) => { if (!v) setDeleting(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar banner</AlertDialogTitle>
            <AlertDialogDescription>
              {deleting
                ? `¿Seguro que deseas eliminar el banner ${deleting.id}? Esta acción no se puede deshacer.`
                : '¿Seguro que deseas eliminar este banner?'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleting(null)}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                if (!deleting) return;
                const target = deleting;
                setDeleting(null);
                await remove(target);
              }}
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
